import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import { cn } from 'src/lib/utils';
import {
  HOUR_12_OPTIONS,
  MINUTE_INTERVAL_OPTIONS,
  buildTimeSlot,
  formatTimeSlotLabel,
  parseTimeSlotParts,
  snapToThirtyMinuteSlot,
  type TimePeriod,
} from 'src/helpers/scheduler-time.helpers';

interface SchedulerTimePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

const pickerButtonClass = (selected: boolean) =>
  cn(
    'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
    selected
      ? 'border-black bg-black text-white'
      : 'border-gray-400 bg-white text-gray-700 hover:bg-gray-200'
  );

export function SchedulerTimePicker({
  id,
  value,
  onChange,
  disabled = false,
  className,
}: SchedulerTimePickerProps) {
  const [open, setOpen] = useState(false);
  const normalizedValue = snapToThirtyMinuteSlot(value);
  const parsed = parseTimeSlotParts(normalizedValue);

  const [hour12, setHour12] = useState(parsed.hour12);
  const [minute, setMinute] = useState<0 | 30>(parsed.minute);
  const [period, setPeriod] = useState<TimePeriod>(parsed.period);

  useEffect(() => {
    const next = parseTimeSlotParts(normalizedValue);
    setHour12(next.hour12);
    setMinute(next.minute);
    setPeriod(next.period);
  }, [normalizedValue]);

  const applyParts = (nextHour: number, nextMinute: 0 | 30, nextPeriod: TimePeriod) => {
    setHour12(nextHour);
    setMinute(nextMinute);
    setPeriod(nextPeriod);
    onChange(buildTimeSlot(nextHour, nextMinute, nextPeriod));
  };

  const displayLabel = formatTimeSlotLabel(normalizedValue);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center gap-2 rounded border border-gray-400 bg-white pl-4 pr-4 text-left text-base text-black',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`Schedule time, ${displayLabel} Eastern`}
        >
          <Clock
            className="size-4 shrink-0 text-gray-500"
            aria-hidden
          />
          <span className="flex-1 truncate">
            {displayLabel}
          </span>
          <span className="shrink-0 text-sm text-gray-500">
            ET
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto min-w-[280px] border border-gray-400 bg-white p-4 shadow-md"
        aria-label="Choose schedule time"
      >
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500">
          Set time (30-minute intervals)
        </p>

        <div className="space-y-4">
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">
              Hour
            </p>
            <div className="grid grid-cols-4 gap-2">
              {HOUR_12_OPTIONS.map((hour) => (
                <button
                  key={hour}
                  type="button"
                  className={pickerButtonClass(hour === hour12)}
                  onClick={() => applyParts(hour, minute, period)}
                >
                  {hour}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">
              Minute
            </p>
            <div className="grid grid-cols-2 gap-2">
              {MINUTE_INTERVAL_OPTIONS.map((min) => (
                <button
                  key={min}
                  type="button"
                  className={pickerButtonClass(minute === min)}
                  onClick={() => applyParts(hour12, min, period)}
                >
                  :
                  {String(min).padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">
              Period
            </p>
            <div className="grid grid-cols-2 gap-2">
              {(['AM', 'PM'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  className={pickerButtonClass(period === value)}
                  onClick={() => applyParts(hour12, minute, value)}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-4 text-center text-sm font-medium text-black">
          {formatTimeSlotLabel(buildTimeSlot(hour12, minute, period))}
          {' '}
          ET
        </p>
      </PopoverContent>
    </Popover>
  );
}
